import { z } from "zod";

import { fetchWithError, useParsedQueries, useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { lineStringSchema } from "../geojson";
import { paginatedSchema } from "../pagination";

import { baseAssetWithTimeseriesSchema } from "./assetsBase";

export const culvertListItemSchema = baseAssetWithTimeseriesSchema.extend({
  geometry: lineStringSchema,
  type: z.string(),
  bed_level_upstream: z.number().nullable(),
  bed_level_downstream: z.number().nullable(),
  width: z.number().nullable(),
  length: z.number().nullable(),
  height: z.number().nullable(),
  material: z.number().nullable(),
  shape: z.number().nullable(),
  description: z.string().nullable(),
  /* Allowed flow direction appears missing */
});

export type CulvertListItem = z.infer<typeof culvertListItemSchema>;

export const culvertListSchema = paginatedSchema(culvertListItemSchema);
export type CulvertList = z.infer<typeof culvertListSchema>;

export const culvertSchema = culvertListItemSchema;
export type Culvert = z.infer<typeof culvertSchema>;

export function useCulverts(params = {}) {
  const url = combineUrlAndParams("/api/v4/culverts/", params);
  return useParsedQuery<CulvertList>(
    ["lizard-api-v4", "culverts", url],
    () => fetchWithError(url),
    (json: unknown) => culvertListSchema.parse(json)
  );
}

export function useCulvert(id: number) {
  return useParsedQuery<Culvert>(
    ["lizard-api-v4", "culverts", id],
    () => fetchWithError(`/api/v4/culverts/${id}/`),
    (json: unknown) => culvertSchema.parse(json)
  );
}

export function useMultipleCulverts(ids: number[]) {
  return useParsedQueries(
    ids.map((id) => {
      return {
        queryKey: ["lizard-api-v4", "culverts", id],
        fetchFunction: () => fetchWithError(`/api/v4/culverts/${id}/`),
        parseFunction: (json: unknown) => culvertSchema.parse(json),
      };
    })
  );
}
